import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import { css } from '@emotion/css';
import useInspireOptions from '../../hooks/useInspireOptions';
import { WeeklySummary, TranslationState } from '@amzn/aet-summaries-kit';
import noDataPic from '../../Assets/NoData.svg';
import * as styles from './styles';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column, { ColumnProps } from '@amzn/meridian/column/column';
import Icon from '@amzn/meridian/icon';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Divider from '@amzn/meridian/divider';
import Pagination from '@amzn/meridian/pagination';
import calendarTokens from '@amzn/meridian-tokens/base/icon/calendar';
import chevronRightTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';
import { ViewContext } from './types';
import CSAFeedbackModal from './FeedbackModals/CSAFeedbackModal';
import ManagerFeedbackModal from './FeedbackModals/ManagerFeedbackModal';
import { usePaginatedSummaries } from './hooks/usePaginatedSummaries';
import CenteredLoader from '../../commons/CenteredLoader';
import { SummariesErrorAlert } from './common/Alerts';
import { useGetIsSummaryFromLastWeek } from './utils/weekUtils';
import { findContactIds } from './utils/contactIdUtils';
import EnvConfigUtil from '../../config/EnvConfigUtil';
import ContactListeningModal from '../ContactListening/ContactListeningModal';
import ContactListening from '../ContactListening/ContactListening';
import {
  ContactData,
  defaultContactData,
} from '../../api/ContactListening/schema';
import DataState from '../../Types/DataState';
import { QueryVariablesContactListeningData } from '../../api/Naws';
import useDidRequestorProvideFeedback from './hooks/useDidRequestorProvideFeedback';
import { User } from '../../Types';
import { getCscEntityPrefix } from '../../config/Config';
import {
  CSA_SUMMARIES,
  CSA_SUMMARIES_SLOT,
  SummaryDeletionReasons,
} from './constants';
import { RowWidths } from '@amzn/meridian/row/row';
import {
  ClickstreamTypes,
  useClickstreamTags,
} from '../../hooks/useClickstreamTags';
import { awsRum } from '../../config/cloudWatchRUM';
import Alert from '@amzn/meridian/alert';

export interface CSASummariesContentProps {
  spacingInset?: ColumnProps['spacingInset'];
  displayedCsa: User;
  viewContext: ViewContext;
  refetch?: Function;
  initialSummary?: WeeklySummary | null; // For TM view to pass pre-loaded summary
  setParentExpanded?: (result: boolean) => void;
}

export const CSASummariesContent = ({
  spacingInset,
  displayedCsa,
  viewContext,
  refetch,
  initialSummary,
  setParentExpanded,
}: CSASummariesContentProps) => {
  const { isTm, requesterUsername } = useInspireOptions();

  const {
    summaries,
    summariesToWeek,
    currentPage,
    totalPages,
    isLoading,
    error,
    goToPage,
    isPaginationMode,
    enablePagination,
    refetchPage,
    hasExhaustedTranslationRetries,
  } = usePaginatedSummaries(displayedCsa.alias, initialSummary);

  const alreadyProvidedFeedback = useDidRequestorProvideFeedback(summaries[0]);

  const seeMoreLinkClickstreamData = useClickstreamTags({
    slotId: CSA_SUMMARIES_SLOT,
    type: ClickstreamTypes.Link,
    feature: CSA_SUMMARIES,
    action: 'See Previous Summaries',
  });

  const paginationClickstreamData = useClickstreamTags({
    slotId: CSA_SUMMARIES_SLOT,
    type: ClickstreamTypes.Element,
    feature: CSA_SUMMARIES,
    action: 'Navigate Pagination',
  });

  // this callback is to support parent components behaviour based on the data fetched in this component
  // i.e. CSA view is initially closed, and we open it if they haven't provided feedback yet
  useEffect(() => {
    // we are also checking !isTm here as a TM can view a CSA dashboard, and we do not want to auto expand/close in that situation
    // note the difference between isTm (requestor) and isProfileTm (viewed profile)
    if (!isLoading && summaries[0] && !alreadyProvidedFeedback && !isTm) {
      setParentExpanded?.(true); // open the container if the CSA has not provided feedback
    }
  }, [isLoading, summaries, alreadyProvidedFeedback, setParentExpanded, isTm]);

  if (isLoading) {
    return <CenteredLoader data-testid='summaries-content-loader' />;
  }

  if (error) {
    return <SummariesErrorAlert data-testid='summaries-content-error' />;
  }

  return (
    <Column spacingInset={spacingInset} spacing='300'>
      {summariesToWeek.map(({ week, summary }, index) => (
        <React.Fragment key={week.toString()}>
          {index >= 1 && (
            <Row spacingInset='400 none'>
              <Divider />
            </Row>
          )}
          <SummaryWeekContent
            week={week}
            summary={summary}
            displayedCsa={displayedCsa}
            viewContext={viewContext}
            isTm={isTm}
            requesterUsername={requesterUsername}
            refetch={refetch}
            isPaginationMode={isPaginationMode}
            enablePagination={enablePagination}
            refetchPage={refetchPage}
            hasExhaustedTranslationRetries={hasExhaustedTranslationRetries}
          />
        </React.Fragment>
      ))}

      {isPaginationMode && (
        <>
          <Row spacingInset='400 none'>
            <Divider />
          </Row>
          <Row alignmentHorizontal='center' spacingInset='none none 200 none'>
            <Pagination
              onChange={goToPage}
              numberOfPages={totalPages}
              currentPage={currentPage}
              data-testid='pagination'
              {...paginationClickstreamData}
            />
          </Row>
        </>
      )}

      {!isTm && viewContext === ViewContext.SELF && (
        <CSAActions
          summary={summaries[0]}
          isPaginationMode={isPaginationMode}
          enablePagination={enablePagination}
          setParentExpanded={setParentExpanded}
        />
      )}

      {/* Show "See previous highlights" link for TMs when not in pagination mode and we at least one summary
       */}
      {isTm && !isPaginationMode && summaries[0] && (
        <Box
          spacingInset='500 none 500 none'
          data-testid='tm-see-previous-highlights'
        >
          <Link onClick={enablePagination} {...seeMoreLinkClickstreamData}>
            <FormattedMessage
              id='cs_perfportal_see_previous_highlights'
              defaultMessage='See previous highlights'
            />
          </Link>
        </Box>
      )}
    </Column>
  );
};

interface SummaryWeekContentProps {
  week: moment.Moment;
  summary?: WeeklySummary;
  displayedCsa: User;
  viewContext: ViewContext;
  isTm: boolean;
  requesterUsername: string;
  refetch?: Function;
  isPaginationMode: boolean;
  enablePagination: () => void;
  refetchPage: () => void;
  hasExhaustedTranslationRetries: boolean;
}

const SummaryWeekContent = ({
  week,
  summary,
  displayedCsa,
  viewContext,
  isTm,
  requesterUsername,
  refetch,
  isPaginationMode,
  refetchPage,
  enablePagination,
  hasExhaustedTranslationRetries,
}: SummaryWeekContentProps) => {
  // Add state for contact listening modal
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [recordingContactId, setRecordingContactId] = useState('');
  const [contactListeningData, setContactListeningData] =
    useState<ContactData>(defaultContactData);
  const [contactListeningDataState, setContactListeningDataState] =
    useState<string>(DataState.INITIALIZED);
  const [contactListeningParams, setContactListeningParams] =
    useState<QueryVariablesContactListeningData>();

  const contentClickstream = useClickstreamTags({
    type: ClickstreamTypes.Widget,
    action: 'impression',
    slotId: CSA_SUMMARIES_SLOT,
    feature: CSA_SUMMARIES,
    'data-csa-c-slot-id': 'csa-summaries-content',
  });

  // Show empty state if no summary for last week
  if (!summary) {
    return (
      <EmptyState isPaginationMode={isPaginationMode} week={week}>
        {viewContext === ViewContext.SELF && !isTm ? (
          <FormattedMessage
            id='cs_perfportal_no_weekly_summary_you'
            defaultMessage='Weekly highlights will appear here when you handle contacts for supported marketplaces and channels'
          />
        ) : (
          <FormattedMessage
            id='cs_perfportal_no_weekly_summary_named'
            defaultMessage="{displayName}'s Weekly highlights will appear here when they handle contacts for supported marketplaces and channels"
            values={{ displayName: displayedCsa.displayName }}
          />
        )}
      </EmptyState>
    );
  }

  // show deletion message if deleted due to sandfire
  if (
    summary.isDeleted &&
    summary.deletedReason === SummaryDeletionReasons.SANDFIRE
  ) {
    return (
      <EmptyState
        isPaginationMode={isPaginationMode}
        week={week}
        rowWidths={['grid-8']}
      >
        <FormattedMessage
          id='cs_perfportal_summary_privacy_deleted'
          defaultMessage="This week's highlight has been removed due to privacy regulations"
        />
      </EmptyState>
    );
  }

  const canGiveManagerFeedback =
    isTm &&
    !summary.feedbackProviders?.includes(requesterUsername) &&
    (viewContext === ViewContext.SELF ||
      viewContext === ViewContext.DIRECT_REPORT_FROM_TEAM);

  return (
    <Column
      spacing='300'
      data-testid='summaries-content'
      {...contentClickstream}
    >
      <Row alignmentHorizontal='justify' alignmentVertical='top'>
        <SummaryDate date={moment(summary.date)} />
        {canGiveManagerFeedback && (
          <TMFeedbackButton
            summary={summary}
            refetch={isPaginationMode ? refetchPage : refetch}
          />
        )}
      </Row>
      <FormattedMessage
        id='cs_perfportal_highlight_based_on_contacts_served'
        defaultMessage='This highlight is based on insights from {chatCount} of the contacts you handled in supported marketplaces this week.'
        values={{
          chatCount: summary.contact_count,
        }}
      />

      <Box spacingInset='300 none none none'>
        <SummaryText
          summary={summary}
          displayedCsa={displayedCsa}
          setContactListeningData={setContactListeningData}
          setContactListeningDataState={setContactListeningDataState}
          setContactListeningParams={setContactListeningParams}
          setRecordingContactId={setRecordingContactId}
          setShowRecordingModal={setShowRecordingModal}
          hasExhaustedTranslationRetries={hasExhaustedTranslationRetries}
        />
      </Box>

      {/* Add ContactListeningModal */}
      <ContactListeningModal
        loadingState={contactListeningDataState}
        showModal={showRecordingModal}
        setShowModal={setShowRecordingModal}
        contactId={recordingContactId}
        contactListeningData={contactListeningData}
        contactListeningParams={contactListeningParams}
        CLClickstream={{
          slotID: 'open-contact-listening-modal',
          tag1: 'CSA Summaries',
          tag2: 'Summary Text',
          tag3: 'Contact Listening Modal',
        }}
      />
    </Column>
  );
};

interface SummaryTextProps {
  summary: WeeklySummary;
  displayedCsa: User;
  setContactListeningData: React.Dispatch<React.SetStateAction<ContactData>>;
  setContactListeningDataState: React.Dispatch<React.SetStateAction<string>>;
  setContactListeningParams: React.Dispatch<
    React.SetStateAction<QueryVariablesContactListeningData | undefined>
  >;
  setRecordingContactId: React.Dispatch<React.SetStateAction<string>>;
  setShowRecordingModal: React.Dispatch<React.SetStateAction<boolean>>;
  hasExhaustedTranslationRetries: boolean;
}

const SummaryText = ({
  summary,
  displayedCsa,
  setContactListeningData,
  setContactListeningDataState,
  setContactListeningParams,
  setRecordingContactId,
  setShowRecordingModal,
  hasExhaustedTranslationRetries,
}: SummaryTextProps) => {
  // Check if the current region is NA
  const isNA = EnvConfigUtil.isNARegion();

  // Check if translation is still pending after exhausting retries
  const isTranslationPendingAfterRetries =
    summary.translationState === TranslationState.PENDING &&
    hasExhaustedTranslationRetries;

  // Prefer translated summary if available
  const summaryText = summary.translatedSummary || summary.summary;

  // TODO - SSA may begin including agent name directly.  if they start doing that, this can be removed.
  const textWithAgentName = useMemo(() => {
    const firstName = displayedCsa.displayName.split(' ')[0];

    if (firstName) {
      return summaryText.replace(`@${summary.alias}`, firstName);
    }
    return summaryText;
  }, [summaryText, summary.alias, displayedCsa.displayName]);

  // Define segment type
  type TextSegment = {
    type: 'text' | 'contactId';
    content: string;
  };

  // log view with RUM
  useEffect(() => {
    // logging both clickstream and rum for now to help with analysis
    awsRum?.recordEvent('csa_summaries_impression', {
      summary_uuid: summary.uuid,
      marketplaces: JSON.stringify(summary.marketplaces),
    });
  }, [summary]);

  // Find contact IDs in the text (only for NA region)
  const segments = useMemo<TextSegment[]>(() => {
    // If not in NA region, just return the text as a single segment
    if (!isNA) {
      return [{ type: 'text', content: textWithAgentName }];
    }

    const contactIdMatches = findContactIds(textWithAgentName);

    if (contactIdMatches.length === 0) {
      return [{ type: 'text', content: textWithAgentName }];
    }

    const segments: TextSegment[] = [];
    let lastIndex = 0;

    contactIdMatches.forEach(match => {
      // Add text before the match
      if (match.start > lastIndex) {
        segments.push({
          type: 'text',
          content: textWithAgentName.substring(lastIndex, match.start),
        });
      }

      // Add the contact ID
      segments.push({
        type: 'contactId',
        content: match.contactId,
      });

      lastIndex = match.end;
    });

    // Add remaining text
    if (lastIndex < textWithAgentName.length) {
      segments.push({
        type: 'text',
        content: textWithAgentName.substring(lastIndex),
      });
    }

    return segments;
  }, [textWithAgentName]);

  if (isTranslationPendingAfterRetries) {
    return (
      <Box spacingInset='400'>
        <Alert type='error'>
          <FormattedMessage
            id='cs_perfportal_error_refresh_page'
            defaultMessage='Something went wrong. Please try refreshing the page. If the problem persists, please submit a SIM ticket for us to troubleshoot.'
          />
        </Alert>
      </Box>
    );
  }

  return (
    <Text>
      {segments.map((segment, index) => {
        if (segment.type === 'contactId') {
          return (
            <ContactListening
              key={index}
              contactHandlerId={
                getCscEntityPrefix() + ':' + displayedCsa.userID
              }
              contactID={segment.content}
              setContactListening={setContactListeningData}
              setContactListeningState={setContactListeningDataState}
              setContactListeningParams={setContactListeningParams}
              setRecordingContactId={setRecordingContactId}
              setShowRecordingModal={setShowRecordingModal}
              displayAsLink={true}
              clickstreamTags={{
                'data-csa-c-slot-id': 'csa-summaries-contact-id-slot',
                'data-csa-c-content-id': `csa-summaries-contact-link-for-${segment.content}`,
                'data-csa-c-tag1': 'CSA Summaries',
                'data-csa-c-tag2': 'Summary Text',
                'data-csa-c-contactid': segment.content,
              }}
              contactStartDate={moment(summary.date)
                .subtract(1, 'week')
                .format('YYYY-MM-DD')}
              contactEndDate={moment(summary.date)
                .add(1, 'week')
                .format('YYYY-MM-DD')}
              CLClickstream={{
                slotID: 'open-contact-listening-modal',
                tag1: 'CSA Summaries',
                tag2: 'Summary Text',
                tag3: 'Contact Listening Modal',
              }}
            />
          );
        }

        // Handle newlines in text segments
        const sectionParts = segment.content.split('\n\n');
        return (
          <React.Fragment key={index}>
            {sectionParts.map((part, partIndex) => (
              <React.Fragment key={partIndex}>
                {part}
                {partIndex < sectionParts.length - 1 && (
                  <Box spacingInset='300 none none none' />
                )}
              </React.Fragment>
            ))}
          </React.Fragment>
        );
      })}
    </Text>
  );
};

interface CSAActionsProps {
  summary?: WeeklySummary;
  isPaginationMode: boolean;
  enablePagination: () => void;
  setParentExpanded?: (expanded: boolean) => void;
}

const CSAActions = ({
  summary,
  isPaginationMode,
  enablePagination,
  setParentExpanded,
}: CSAActionsProps) => {
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [providedFeedback, setProvidedFeedback] = useState(false);
  const isSummaryFromLastWeek = useGetIsSummaryFromLastWeek(summary);
  const alreadyProvidedFeedback = useDidRequestorProvideFeedback(summary);

  const acknowledgeClickstream = useClickstreamTags({
    type: ClickstreamTypes.Button,
    slotId: CSA_SUMMARIES_SLOT,
    feature: CSA_SUMMARIES,
    action: 'Acknowledge Highlight',
  });

  const seePreviousHighlightsClickstream = useClickstreamTags({
    type: ClickstreamTypes.Button,
    slotId: CSA_SUMMARIES_SLOT,
    feature: CSA_SUMMARIES,
    action: 'See Previous Highlights',
  });

  const closeHighlightsClickstream = useClickstreamTags({
    type: ClickstreamTypes.Button,
    slotId: CSA_SUMMARIES_SLOT,
    feature: CSA_SUMMARIES,
    action: 'Close Highlights',
  });

  if (
    summary &&
    isSummaryFromLastWeek &&
    !providedFeedback &&
    !alreadyProvidedFeedback
  ) {
    return (
      <>
        <CSAFeedbackModal
          isOpen={feedbackModalOpen}
          onClose={() => setFeedbackModalOpen(false)}
          summary={summary}
          onSuccess={() => {
            setProvidedFeedback(true);
          }}
        />
        <Row alignmentHorizontal='end'>
          <Button
            size='small'
            onClick={() => {
              setFeedbackModalOpen(true);
            }}
            data-testid='csa-actions-read-highlights'
            {...acknowledgeClickstream}
          >
            <FormattedMessage
              id='cs_perfportal_ive_read_my_highlight'
              defaultMessage="I've read my Weekly highlight"
            />
          </Button>
        </Row>
      </>
    );
  }

  return (
    <Row alignmentHorizontal='end'>
      {/* Show "See previous highlights" button for CSAs when not in pagination mode and they have older summaries */}
      {!isPaginationMode && summary && (
        <Button
          type='tertiary'
          size='small'
          onClick={enablePagination}
          data-testid='csa-actions-see-previous-highlights'
          {...seePreviousHighlightsClickstream}
        >
          <FormattedMessage
            id={'cs_perfportal_see_previous_highlights'}
            defaultMessage='See previous highlights'
          />
        </Button>
      )}
      <Button
        size='small'
        onClick={() => {
          setParentExpanded?.(false);
        }}
        data-testid='csa-actions-close-highlights'
        {...closeHighlightsClickstream}
      >
        <FormattedMessage
          id={'cs_perfportal_close_weekly_highlights'}
          defaultMessage='Close Weekly Highlights'
        />
      </Button>
    </Row>
  );
};

interface TMFeedbackButtonProps {
  summary: WeeklySummary;
  refetch?: Function;
}

const TMFeedbackButton = ({ summary, refetch }: TMFeedbackButtonProps) => {
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  const giveFeedbackClickstream = useClickstreamTags({
    type: ClickstreamTypes.Button,
    slotId: CSA_SUMMARIES_SLOT,
    feature: CSA_SUMMARIES,
    action: 'Give Manager Feedback',
  });

  return (
    <>
      <ManagerFeedbackModal
        isOpen={feedbackModalOpen}
        onClose={() => {
          setFeedbackModalOpen(false);
        }}
        onSuccess={() => {
          setFeedbackSubmitted(true);
          refetch?.();
        }}
        summary={summary}
      />
      {!feedbackSubmitted && (
        <Button
          type='tertiary'
          size='small'
          className={css({
            paddingLeft: '10px',
            paddingRight: '4px', // icon has some implicit padding, so we must have less on the right side to make the button pixel perfect with the mocks
          })}
          onClick={() => {
            setFeedbackModalOpen(true);
          }}
          {...giveFeedbackClickstream}
        >
          <Row spacing='200' spacingInset='none none none none'>
            <Box>
              <FormattedMessage
                id='cs_perfportal_give_feedback_on_weekly_highlight'
                defaultMessage='Give feedback on Weekly highlight'
              />
            </Box>
            <Icon tokens={chevronRightTokens} />
          </Row>
        </Button>
      )}
    </>
  );
};

interface SummaryDateProps {
  date: moment.Moment;
}

const SummaryDate = ({ date }: SummaryDateProps) => {
  return (
    <Row spacing='200' alignmentHorizontal='start' width='100%'>
      <Icon tokens={calendarTokens} className={styles.teal500Element} />
      <Text type='b100'>
        <span className={styles.teal500Element}>
          <FormattedMessage
            id='cs_perfportal_week_of'
            defaultMessage='Week of {startDate} - {endDate}'
            // note: summary.date == the day the summary was generated.
            // they are generated on Sundays for the prev weeks contacts,
            // so we adjust this before we display
            values={{
              startDate: date.clone().subtract(1, 'week').format('MMMM D'),
              endDate: date.clone().subtract(1, 'day').format('MMMM D'),
            }}
          />
        </span>
      </Text>
    </Row>
  );
};

interface EmptyStateProps {
  isPaginationMode: boolean;
  week: moment.Moment;
  children: React.ReactNode;
  rowWidths?: RowWidths;
}

const EmptyState = ({
  children,
  isPaginationMode,
  week,
  rowWidths,
}: EmptyStateProps) => {
  return (
    <Column
      spacing='300'
      alignmentVertical='center'
      data-testid='summaries-content-empty-state'
    >
      <Column alignmentHorizontal='center'>
        {isPaginationMode && <SummaryDate date={week} />}
        <img src={noDataPic} alt='No data' className={styles.noDataImage} />
        <Row
          alignmentHorizontal='center'
          alignmentVertical='center'
          widths={rowWidths ?? ['grid-8']}
        >
          <Text alignment='center' type='b100'>
            {children}
          </Text>
        </Row>
      </Column>
    </Column>
  );
};

export default CSASummariesContent;
